import { Injectable, OnDestroy } from '@angular/core';
import { ChatService } from '@common/services/chat.service';
import { currentUserSubject } from '@common/services/user.service';
import { BehaviorSubject, shareReplay, Subscription } from 'rxjs';

export interface ChatUnreadCounts {
    total: number;
    general: number;
    vesselVisit: number;
    serviceRequest: number;
    storageDocument: number;
    amendmentDocument: number;
    sent: number;
}

@Injectable({
    providedIn: 'root'
})
export class ChatCounterService implements OnDestroy {
    private countsSubject = new BehaviorSubject<ChatUnreadCounts>({
        total: 0,
        general: 0,
        vesselVisit: 0,
        serviceRequest: 0,
        storageDocument: 0,
        amendmentDocument: 0,
        sent: 0
    });

    public counts$ = this.countsSubject.asObservable();

    private pollingSubscription: Subscription;

    constructor(private chatService: ChatService) {}

    /**
     * Start polling for unread counts
     */
    startPolling(): void {
        // Stop any existing polling
        this.stopPolling();

        // Set up interval polling
        this.pollingSubscription = this.chatService
            .getUnreadCounts()
            .pipe(shareReplay(1))
            .subscribe((counts) => {
                this.updateCounts(counts);
            });
    }

    /**
     * Stop polling for unread counts
     */
    stopPolling(): void {
        if (this.pollingSubscription) {
            this.pollingSubscription.unsubscribe();
            this.pollingSubscription = null;
        }
    }

    /**
     * Manually refresh counts (called after reading messages, etc.)
     */
    refreshCounts(): void {
        const user = currentUserSubject.getValue();
        if (!user) {
            this.resetCounts();
            return;
        }

        this.chatService.getUnreadCounts().subscribe((counts) => {
            this.updateCounts(counts);
        });
    }

    getCurrentCounts(): ChatUnreadCounts {
        return this.countsSubject.getValue();
    }

    private updateCounts(counts: any): void {
        const newCounts: ChatUnreadCounts = {
            total: counts.total || 0,
            general: counts.general || 0,
            vesselVisit: counts.vesselVisit || 0,
            serviceRequest: counts.serviceRequest || 0,
            storageDocument: counts.storageDocument || 0,
            amendmentDocument: counts.amendmentDocument || 0,
            sent: counts.sent || 0
        };

        this.countsSubject.next(newCounts);
    }

    private resetCounts(): void {
        this.countsSubject.next({
            total: 0,
            general: 0,
            vesselVisit: 0,
            serviceRequest: 0,
            storageDocument: 0,
            amendmentDocument: 0,
            sent: 0
        });
    }

    ngOnDestroy(): void {
        this.stopPolling();
    }
}
